import { RouteConstants } from '@/features/router/RouteConstants';

export type CampaignCreditChannel = 'whatsapp' | 'sms' | 'email';

export type CreditPackageTier = {
  id: string;
  name: string;
  volume: number;
  priceNet: number;
  /** Savings badge text for email larger tiers (from price list only). */
  savingsBadge?: string;
  isPopular?: boolean;
};

export type CreditSalesContact = {
  phone: string;
  email: string;
};

export const CREDIT_TYPE_BY_CHANNEL: Record<CampaignCreditChannel, string> = {
  whatsapp: 'WHATSAPP',
  sms: 'SMS',
  email: 'EMAIL',
};

/**
 * WhatsApp is priced per destination country, so the credit-service wants `countries`
 * (ISO code -> message count) rather than a flat quantity. Pricing is Italy-only by product
 * decision, so the whole volume is attributed to IT — not a placeholder to be inferred from
 * the audience.
 */
export const WHATSAPP_PRICING_COUNTRY = 'IT';

export const PACKAGES_ROUTE_BY_CHANNEL: Record<CampaignCreditChannel, string> = {
  whatsapp: RouteConstants.whatsappPackages,
  sms: RouteConstants.smsPackages,
  email: RouteConstants.emailPackages,
};

export const CREDIT_SALES_CONTACT: CreditSalesContact = {
  phone: '+39 02 1234 5678',
  email: 'sales@audiencerate.com',
};

export const WHATSAPP_PACKAGE_CATEGORIES = [
  { id: 'marketing', labelKey: 'credits.packages.categories.marketing' },
  { id: 'utility', labelKey: 'credits.packages.categories.utility' },
  { id: 'authentication', labelKey: 'credits.packages.categories.authentication' },
] as const;

/** Volume tiers shared across WhatsApp categories; prices vary by category. */
export const WHATSAPP_PACKAGE_SIZES = [
  { id: 'starter', name: 'Starter', volume: 1_000 },
  { id: 'growth', name: 'Growth', volume: 5_000, isPopular: true },
  { id: 'business', name: 'Business', volume: 10_000 },
  { id: 'enterprise', name: 'Enterprise', volume: 25_000 },
] as const;

export const WHATSAPP_PACKAGE_PRICES: Record<string, number[]> = {
  marketing: [80, 350, 650, 1_500],
  utility: [60, 260, 480, 1_100],
  authentication: [50, 220, 400, 900],
};

export const SMS_PACKAGE_TIERS: CreditPackageTier[] = [
  { id: 'sms-1k', name: '1.000', volume: 1_000, priceNet: 40 },
  { id: 'sms-5k', name: '5.000', volume: 5_000, priceNet: 180, isPopular: true },
  { id: 'sms-10k', name: '10.000', volume: 10_000, priceNet: 320 },
  { id: 'sms-25k', name: '25.000', volume: 25_000, priceNet: 700 },
  { id: 'sms-50k', name: '50.000', volume: 50_000, priceNet: 1_250 },
  { id: 'sms-100k', name: '100.000', volume: 100_000, priceNet: 2_200 },
];

export const EMAIL_PACKAGE_TIERS: CreditPackageTier[] = [
  { id: 'email-10k', name: '10.000', volume: 10_000, priceNet: 50 },
  { id: 'email-50k', name: '50.000', volume: 50_000, priceNet: 200, isPopular: true },
  { id: 'email-100k', name: '100.000', volume: 100_000, priceNet: 350, savingsBadge: '−12%' },
  { id: 'email-250k', name: '250.000', volume: 250_000, priceNet: 750, savingsBadge: '−18%' },
  { id: 'email-500k', name: '500.000', volume: 500_000, priceNet: 1_300, savingsBadge: '−22%' },
  { id: 'email-1m', name: '1.000.000', volume: 1_000_000, priceNet: 2_200, savingsBadge: '−28%' },
];

export const getWhatsappPackageTiers = (categoryId: string): CreditPackageTier[] => {
  const prices = WHATSAPP_PACKAGE_PRICES[categoryId] ?? WHATSAPP_PACKAGE_PRICES['marketing'];
  return WHATSAPP_PACKAGE_SIZES.map((size, index) => ({
    id: size.id,
    name: size.name,
    volume: size.volume,
    priceNet: prices[index] ?? 0,
    isPopular: 'isPopular' in size ? size.isPopular : false,
  }));
};

export const formatEuroNet = (value: number, locale = 'it-IT'): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
